import React from 'react'
import { useTabs } from '../../contexts/TabsContext'
import { StickyNote } from 'lucide-react'
import ViewNotePage from '../../pages/notes/ViewNotePage'
import EditNotePage from '../../pages/notes/EditNotePage'
import CreateNotePage from '../../pages/notes/CreateNotePage'
import LazyNoteEditor from '../editors/LazyNoteEditor'
import EditorRegistry from '../editors/EditorRegistry'
import { detectNoteType, isSinglePurposeNote } from '../../utils/noteTypeDetection'
import { useNotes } from '../../contexts/NotesContext'
import { useNavigate } from 'react-router-dom'

/**
 * TabContent Component
 * 
 * Renders the content of the active tab
 */
const TabContent = () => {
  const { tabs, activeTabId, getActiveTab, closeTab } = useTabs()
  const { createNote } = useNotes()
  const navigate = useNavigate()
  
  const activeTab = getActiveTab()
  
  // Show empty state if no tabs
  if (tabs.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="text-center">
          <StickyNote className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500" />
          <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">
            No notes open
          </h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Open a note to get started or create a new one.
          </p>
        </div>
      </div>
    )
  }
  
  // Show message if no active tab (shouldn't happen but good fallback)
  if (!activeTab) {
    return (
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="text-center">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            No active tab selected.
          </p>
        </div>
      </div>
    )
  }
  
  // Handle single purpose note save
  const handleSinglePurposeNoteSave = async (noteData) => {
    try {
      const savedNote = await createNote(noteData)
      closeTab(activeTab.id)
      // Navigate to the saved note
      if (savedNote && savedNote.noteId) {
        navigate(`/notes/${savedNote.noteId}`)
      }
    } catch (error) {
      console.error('Failed to save single purpose note:', error)
      throw error
    }
  }

  // Handle single purpose note cancel
  const handleSinglePurposeNoteCancel = () => {
    closeTab(activeTab.id)
  }

  // Render all tabs but only show the active one to preserve state
  const renderAllTabs = () => {
    return tabs.map(tab => {
      const isActive = tab.id === activeTabId
      
      const renderTabComponent = () => {
        switch (tab.type) {
          case 'view':
            return <ViewNotePage tabId={tab.id} noteId={tab.noteId} />
          case 'edit':
            // Use EditorRegistry to route to appropriate editor based on note type
            return (
              <EditorRegistry 
                tabId={tab.id} 
                noteId={tab.noteId} 
                note={tab.note}
                isInTab={true}
              />
            )
          case 'new':
            // Check if this is a single purpose note
            const noteType = tab.noteType || detectNoteType(tab.note)
            if (isSinglePurposeNote(noteType)) {
              return (
                <LazyNoteEditor
                  note={tab.note}
                  onSave={handleSinglePurposeNoteSave}
                  onCancel={handleSinglePurposeNoteCancel}
                  isInTab={true}
                  tabId={tab.id}
                />
              )
            }
            // Standard note creation
            return <CreateNotePage tabId={tab.id} />
          default:
            return (
              <div className="flex-1 flex items-center justify-center p-8">
                <div className="text-center">
                  <p className="text-sm text-red-500">
                    Unknown tab type: {tab.type}
                  </p>
                </div>
              </div>
            )
        }
      }

      return (
        <div
          key={tab.id}
          className={`h-full w-full ${isActive ? 'block' : 'hidden'}`}
        >
          {renderTabComponent()}
        </div>
      )
    })
  }
  
  return (
    <div className="flex-1 flex flex-col bg-white dark:bg-gray-800 w-full max-w-full overflow-x-hidden">
      <div className="flex-1 overflow-y-auto p-4 md:p-6 w-full max-w-full notes-scroll-container right-section-scroll">
        {renderAllTabs()}
      </div>
    </div>
  )
}

export default TabContent 